'use client';

import { useState, useEffect } from 'react';
import Icon from '@/src/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

 const faqs: FAQ[] = [
  {
    question: 'Is my data safe?',
    answer: 'Yes. Fillr uses bank-level encryption, complies with POPIA, and never shares your info.'
  },
  {
    question: 'How does Fillr track trips?',
    answer: 'It uses GPS to detect trips automatically and links them to fuel purchases. Manual logging is optional.'
  },
  {
    question: 'Will it drain my battery?',
    answer: 'No. Fillr runs efficiently in the background, using minimal battery.'
  },
  {
    question: 'Can I use Fillr for taxes?',
    answer: 'Yes! Fillr generates ready-to-submit SARS-compliant reports in PDF or Excel.'
  },
  {
    question: 'Does it work with Uber and Bolt?',
    answer: 'Yes, you can connect both accounts and track all trips automatically.'
  },
  {
    question: 'Can I track multiple vehicles?',
    answer: 'Yes. Premium and Enterprise plans let you track unlimited vehicles separately.'
  },
  {
    question: 'Is fuel tracking accurate?',
    answer: 'Yes, costs are tracked precisely with receipt scanning and GPS trip data.'
  },
  {
    question: 'Can I try Premium first?',
    answer: 'Yes! Enjoy a 14-day free trial with no credit card required.'
  },
  {
    question: 'What happens if I cancel?',
    answer: 'Your data is kept for 90 days so you can export it before permanent deletion.'
  },
  {
    question: 'Does Fillr work offline?',
    answer: 'Partially. You can log trips and fuel offline; sync occurs when online.'
  }
];


  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const filteredFAQs = isHydrated
    ? faqs.filter(faq =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqs;

  if (!isHydrated) {
    return (
      <section className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
              Frequently Asked Questions
            </h2>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="faq" className="py-20 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-4">
            <Icon name="QuestionMarkCircleIcon" size={20} className="text-primary" />
            <span className="text-primary font-medium">Got Questions?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Everything you need to know about Fillr
          </p>
        </div>

        <div className="mb-8">
          <div className="relative">
            <Icon
              name="MagnifyingGlassIcon"
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary"
            />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white border-2 border-border rounded-xl text-text-primary placeholder-text-secondary focus:outline-none focus:border-primary transition-colors"
            />
          </div>
        </div>

        <div className="space-y-4">
          {filteredFAQs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md overflow-hidden transition-shadow hover:shadow-lg"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-surface transition-colors"
              >
                <span className="font-semibold text-text-primary pr-4">{faq.question}</span>
                <Icon
                  name="ChevronDownIcon"
                  size={24}
                  className={`text-primary flex-shrink-0 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-text-secondary leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredFAQs.length === 0 && (
          <div className="text-center py-12">
            <Icon name="MagnifyingGlassIcon" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">No questions found matching your search.</p>
          </div>
        )}

        {/* <div className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-text-primary mb-2">Still have questions?</h3>
          <p className="text-text-secondary mb-6">
            Our support team is here to help you get started
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-conversion inline-flex items-center justify-center space-x-2">
              <Icon name="ChatBubbleLeftRightIcon" size={20} />
              <span>Live Chat Support</span>
            </button>
            <button className="px-6 py-3 bg-white text-text-primary rounded-lg font-semibold border-2 border-border hover:bg-surface transition-conversion inline-flex items-center justify-center space-x-2">
              <Icon name="EnvelopeIcon" size={20} />
              <span>Email Us</span>
            </button>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default FAQSection;